import PayloadStrategy from "./PayloadStrategy";

/**
 * Follower class representing a follower object.
 *
 * ### Followers parameters
 *
 * |      Key      |Required| Type  |Format|Default|Min\|Max chars|Min\|Max elements|
 * |:-------------:|:------:|:-----:|:----:|:-----:|:------------:|:---------------:|
 * |     email     |&cross; | array | N/A  |  N/A  |   N/A\|N/A   |     1\|N/A      |
 * |  cell_phone   |&cross; | array | N/A  |  N/A  |   N/A\|N/A   |     1\|N/A      |
 * |in_event_status|&cross; | array | N/A  |  N/A  |   N/A\|N/A   |     1\|N/A      |
 * | template_name |&check; |string | N/A  |  N/A  |   1\|1000    |    N/A\|N/A     |
 * |   whatsapp    |&cross; |boolean| N/A  |  N/A  |   N/A\|N/A   |    N/A\|N/A     |
 * |      sms      |&cross; |boolean| N/A  |  N/A  |   N/A\|N/A   |    N/A\|N/A     |
 * |   to_email    |&cross; |boolean| N/A  |  N/A  |   N/A\|N/A   |    N/A\|N/A     |
 * |   event_id    |&check; | number| N/A  |  N/A  |   N/A\|N/A   |    N/A\|N/A     |
 * |      id       |&check; | number| N/A  |  N/A  |   N/A\|N/A   |    N/A\|N/A     |
 *
 * - `email`: List of emails.
 * - `cell_phone`: List of cell phones.
 * - `in_event_status`: List of event statuses.
 * - `template_name`: A valid template name.
 * - `whatsapp`: Valid boolean.
 * - `sms`: Valid boolean.
 * - `to_email`: Valid boolean.
 *
 * ### Example of Follower Object
 *
 * ```json
 * {
 *     "added_on": "2025-02-10 11:55:26",
 *     "cell_phone": [
 *         "+573123456789",
 *         "+573027151722"
 *     ],
 *     "company_id": 1,
 *     "email": [
 *         "jhon.doe@servinformacion.com"
 *     ],
 *     "event_id": 96351,
 *     "id": 30,
 *     "in_event_status": [
 *         "NOT ASSIGNED",
 *         "ASSIGNED",
 *         "RESCHEDULED",
 *         "DELAYED",
 *         "ON THE WAY",
 *         "IN PROGRESS",
 *         "FINALIZED",
 *         "CANCELLED"
 *     ],
 *     "sms": 1,
 *     "template_name": "followers_4",
 *     "to_email": 1,
 *     "update_on": null,
 *     "whatsapp": 1
 * }
 * ```
 */

import {parsePhoneNumberFromString} from "libphonenumber-js";

class Follower extends PayloadStrategy {
	/**
	 * Creates an instance of Follower.
	 * @param {Object} follower - The follower data.
	 */
	constructor(follower) {
		super();

		// Data from the client side
		this.email = follower.email || [];
		this.cell_phone = follower.cell_phone || [];
		this.in_event_status = follower.in_event_status || [
			"NOT ASSIGNED",
			"ASSIGNED",
			"RESCHEDULED",
			"DELAYED",
			"ON THE WAY",
			"IN PROGRESS",
			"FINALIZED",
			"CANCELLED",
		];
		this.template_name = follower.template_name || "";
		this.whatsapp = this.formatBoolean(follower.whatsapp);
		this.sms = this.formatBoolean(follower.sms);
		this.to_email = this.formatBoolean(follower.to_email);
		this.event_id = follower.event_id || null;
		this.language = follower.language || "es_MX"; // Default language is Spanish (es) can be changed to es_MX,en,pt_BR.

		// Data from the server
		this.id = follower.id || null;
		this.company_id = follower.company_id || null;
		this.added_on = follower.added_on || null;
		this.update_on = follower.update_on || null;
	}

	setEventId(eventId) {
		this.event_id = eventId;
		return this;
	}

	setTemplateName(templateName) {
		this.template_name = templateName;
		return this;
	}

	setLanguage(language) {
		this.language = language;
		return this;
	}

	addEmail(email) {
		const emails = email
			.split(",")
			.map((email) => email.trim())
			.filter((email) => email !== "");

		if (emails.length === 0) {
			return;
		}

		const emailSet = new Set(this.email);

		emails.forEach((email) => {
			if (!emailSet.has(email) && this.validateEmail(email)) {
				emailSet.add(email);
			}
		});

		this.email = Array.from(emailSet);
	}

	removeEmail(email) {
		this.email.splice(this.email.indexOf(email), 1);
	}

	validateEmail(email) {
		const re =
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,})$/i;
		return re.test(String(email).toLowerCase());
	}

	addCellPhone(cellPhone, prefix = "+57") {
		const cellPhones = cellPhone
			.split(",")
			.map((cellPhone) => cellPhone.trim())
			.filter((cellPhone) => cellPhone !== "");

		if (cellPhones.length === 0) {
			return;
		}

		const cellPhoneSet = new Set(this.cell_phone);

		cellPhones.forEach((cellPhone) => {
			const phoneNumber = parsePhoneNumberFromString(`${prefix}${cellPhone}`);

			if (!cellPhoneSet.has(cellPhone) && phoneNumber) {
				cellPhoneSet.add(phoneNumber.number);
			}
		});

		this.cell_phone = Array.from(cellPhoneSet);
	}

	removeCellPhone(cellPhone) {
		// remove the cell phone from the list without mutating the original array
		this.cell_phone.splice(this.cell_phone.indexOf(cellPhone), 1);
	}

	validate() {
		if (!this.event_id) return "event_id";

		if (this.email.length === 0 && this.to_email) return "email";

		if (this.cell_phone.length === 0 && (this.sms || this.whatsapp)) return "cell_phone";

		if (this.in_event_status.length === 0) return "in_event_status";

		if (!this.template_name) return "template_name";

		return null;
	}

	splitNumber(phoneNum) {
		const phoneNumber = parsePhoneNumberFromString(phoneNum);

		if (!phoneNumber) {
			return {error: "Invalid phone number"};
		}

		return {
			prefix: `+${phoneNumber.countryCallingCode}`,
			number: phoneNumber.nationalNumber,
			country: phoneNumber.country, // return country code (ej. "US", "ES", "CO")
		};
	}

	formatBoolean(value) {
		if (typeof value === "boolean") return value;
		if (value === "true") return true;
		if (value === "false") return false;
		if (typeof value === "number") return value === 1 ? true : false;
		return false;
	}

	/**
	 * Returns an object representing the payload to create a follower.
	 * This method is an implementation of the abstract method from the parent class
	 *
	 * @returns {Object} The payload object.
	 */
	payloadToCreate() {
		const payload = {
			event_id: this.event_id,
			followers_configs: [
				{
					email: this.email,
					cell_phone: this.cell_phone,
					in_event_status: this.in_event_status,
					template_name: this.template_name,
					whatsapp: this.whatsapp,
					sms: this.sms,
					to_email: this.to_email,
					language: this.language,
				},
			],
		};

		if (payload.followers_configs[0].email.length === 0) {
			delete payload.followers_configs[0].email;
		}

		if (payload.followers_configs[0].cell_phone.length === 0) {
			delete payload.followers_configs[0].cell_phone;
		}

		return payload;
	}

	/**
	 * Returns an object representing the payload to update a follower.
	 * This method is an implementation of the abstract method from the parent class
	 *
	 * @returns {Object} The payload object.
	 */
	payloadToUpdate() {
		const payload = {
			email: this.email,
			cell_phone: this.cell_phone,
			in_event_status: this.in_event_status,
			template_name: this.template_name,
			whatsapp: this.whatsapp,
			sms: this.sms,
			to_email: this.to_email,
			language: this.language,
		};

		if (!payload.to_email || payload.email.length === 0) {
			payload.email = [];
		}

		if ((!payload.sms && !payload.whatsapp) || payload.cell_phone.length === 0) {
			payload.cell_phone = [];
		}

		return payload;
	}

	/**
	 * Returns an array of strings representing the fields to be fetched.
	 * This method is an implementation of the abstract method from the parent class
	 *
	 * @returns {string[]} An array of field names to be fetched.
	 */
	payloadToFetch() {
		return [
			"id",
			"event_id",
			"email",
			"cell_phone",
			"in_event_status",
			"template_name",
			"whatsapp",
			"sms",
			"to_email",
			"language",
		];
	}
}

export default Follower;
